import {
  polygonLineStyle,
  polygonFillStyle
} from './map.policy_hubs.styles'
import center from '@turf/center';
import { canMutateMapLayers, isMapStyleUsable, whenMapLayersMutable } from './mapGuards';

const max_zoom_for_hub_logo = 16;

const removeHubSources = (map: any, sourceKey: string = 'policy_hubs') => {
    if(! map) return;

    const source = map.getSource(sourceKey);
    if(source) map.removeSource(sourceKey);
}

const removeHubsFromMap = (map: any, sourceKey: string = 'policy_hubs') => {
    if (!map) return;
    if (!canMutateMapLayers(map)) {
      whenMapLayersMutable(map, () => removeHubsFromMap(map, sourceKey));
      return;
    }

    try {
      let layer, key;

      key = `${sourceKey}-layer-fill`;
      layer = map.getLayer(`${key}`);
      if (layer) map.removeLayer(`${key}`);

      key = `${sourceKey}-layer-border`;
      layer = map.getLayer(`${key}`);
      if (layer) map.removeLayer(`${key}`);

      key = `${sourceKey}-hub-logo`;
      layer = map.getLayer(`${key}`);
      if (layer) map.removeLayer(`${key}`);

      // Remove event listeners
      map.off('click', key, clickHubLogo);
      map.off('mouseenter', key, mouseEnterHubLogo);
      map.off('mouseleave', key, mouseLeaveHubLogo);
      // mouseleave won't fire for a removed layer, so reset a hover cursor
      map.getCanvas().style.cursor = '';

      removeHubSources(map, sourceKey);
    } catch {
      // Map may already be torn down during route navigation.
    }
}

const isDrawingPolygon = (map) => {
  return map.getContainer().classList.contains('mode-draw_polygon');
}

const mouseEnterHubLogo = (e) => {
  e.target.getCanvas().style.cursor = 'pointer';
}

const mouseLeaveHubLogo = (e) => {
  e.target.getCanvas().style.cursor = '';
}

const clickHubLogo = (e) => {
  const map = window['ddMap'];
  if (!isMapStyleUsable(map)) return;
  // Clicks while drawing place a vertex; don't zoom in on top of that
  if (isDrawingPolygon(map)) return;

  const zoom = map.getZoom();
  map.easeTo({
    center: e.lngLat,
    zoom: zoom + 2
  });
}

async function renderPolygons_fill(map, geojson, sourceKey: string = 'policy_hubs') {
    if(! map) return;
    if(! canMutateMapLayers(map)) {
      whenMapLayersMutable(map, () => renderPolygons_fill(map, geojson, sourceKey));
      return;
    }
    
    const sourceId = sourceKey;
    let layerId = `${sourceId}-layer-fill`
      , source = map.getSource(sourceId);
    const layer = map.getLayer(layerId)
  
    // Add the source if we haven't created them yet
    if (! source) {
      map.addSource(sourceId, {
        type: 'geojson',
        data: geojson,
        // tolerance: 0.5,// Simplies polygons for performance. Defaults to 0.375. Higher is simpler.
        tolerance: 0.2,// Simplies polygons for performance. Defaults to 0.375. Higher is simpler.
        generateId: true, // This ensures that all features have unique IDs
      });
  
      // Set source variable
      source = map.getSource(sourceId);
    }
    // Add layers if these weren't added yet
    if (! layer) {
      // Add polygons (fill + 1px outline) if zoomed in
      map.addLayer({
        id: `${layerId}`,
        source: sourceId,
        type: 'fill',
        paint: polygonFillStyle,
        // Only show hubs fill from a certain zoom level
        // Inspired by: https://stackoverflow.com/a/74550432
        "filter": [
          ">=", ["zoom"],
            ["match", ["get", "geography_type"],
            'no_parking', 0,// 0 = minimum zoom level / always show monitoring zones
            'monitoring', 0,// 0 = minimum zoom level / always show monitoring zones
            max_zoom_for_hub_logo-1]
        ]
      });

      // Add line layer for wider outline/borders, on top of fill layer
      // Info here: https://stackoverflow.com/questions/50351902/in-a-mapbox-gl-js-layer-of-type-fill-can-we-control-the-stroke-thickness/50372832#50372832
      layerId = `${sourceId}-layer-border`;
      map.addLayer({
        id: layerId,
        source: sourceId,
        type: 'line',
        paint: polygonLineStyle,
        // Only show hubs outlines from a certain zoom level
        "filter": [
          ">=", ["zoom"],
            ["match", ["get", "geography_type"],
            'no_parking', 0,// 0 = minimum zoom level / always show monitoring zones
            'monitoring', 0,// 0 = minimum zoom level / always show monitoring zones
            max_zoom_for_hub_logo-1]
        ]
      });

      layerId = `${sourceId}-hub-logo`;
      map.addLayer({
        id: layerId,
        source: sourceId,
        type: 'symbol',
        'layout': {
          'icon-anchor': 'bottom',
          'icon-image': 'hub-icon-mijksenaar',
          "icon-size": [
            'interpolate',
            // Set the exponential rate of change to 1.5
            ['exponential', 1.5],
            ['zoom'],
            // When zoom is 1, icon will be 50% size.
            1,
            0.005,
            // 
            11,
            0.02,
            // When zoom is 10, icon will be 50% size.
            12,
            0.04,
            //
            13,
            0.06,
            // When zoom is 22, icon will be 10% size.
            15,
            0.15
          ],
          'icon-allow-overlap': true,
          'symbol-placement': 'point',
        },
        "filter": [
          "all",
          ["<", ["zoom"], 
            ["match", ["get", "geography_type"],
            'no_parking', 0,// 0 = maximum zoom level (never show mijksenaar logo for no_parking zones)
            'monitoring', 0,// 0 = maximum zoom level (never show mijksenaar logo for monitoring zones)
            max_zoom_for_hub_logo],
          ],
          ["==", ["get", "is_logo_point"], true]
        ]
      });

      map.on('click', layerId, clickHubLogo);
      map.on('mouseenter', layerId, mouseEnterHubLogo);
      map.on('mouseleave', layerId, mouseLeaveHubLogo);

    }
    // If source was already present: Update data
    else {
      // Update the geojson data
      source.setData(geojson);
    }
}

const generateGeojson = (
  hubs,
  selected_policy_hubs,
  hubs_in_drawing_mode
) => {
    let geoJson = {
        type: "FeatureCollection",
        features: []
    }
    
    if(! hubs || ! hubs[0]) {
        return geoJson;
    }
    hubs.forEach(x => {
        // Add the polygon feature
        let feature = {
            "type": "Feature",
            "properties": {
                "id": x.zone_id,
                "name": x.name,
                "geography_type": x.geography_type,
                "phase": x.phase,
                "stop": x.stop,
                "created_at": x.created_at,
                "is_selected": selected_policy_hubs && selected_policy_hubs.indexOf(x.zone_id) > -1 ? 1 : 0,
                "is_in_drawing_mode": hubs_in_drawing_mode && hubs_in_drawing_mode.indexOf(x.zone_id) > -1 ? 1 : 0
            },
            "geometry": x.area.geometry
        }
        geoJson.features.push(feature);

        // Add a point feature for the Mijksenaar logo
        if (x.geography_type !== 'no_parking' && x.geography_type !== 'monitoring') {
            const the_center = center(x.area.geometry);
            const pointFeature = {
                "type": "Feature",
                "properties": {
                    ...feature.properties,
                    "is_logo_point": true
                },
                "geometry": the_center.geometry
            }
            geoJson.features.push(pointFeature);
        }
    });

    return geoJson;   
}

const renderHubs = async (
  map: any,
  hubs: any,
  selected_policy_hubs: any,
  hubs_in_drawing_mode: any,
  sourceKey: string = 'policy_hubs'
) => {
  if(! map) return;

  // Generate feature collection from hubs
  const featureCollection = generateGeojson(
    hubs,
    selected_policy_hubs,
    hubs_in_drawing_mode
  );

  // Remove old polygons first
  removeHubsFromMap(map, sourceKey);

  // Render polygons
  renderPolygons_fill(map, featureCollection, sourceKey);
}

export {
    renderHubs,
    removeHubsFromMap
}
